/**
 * DoSMarkerLayer.tsx — Marker posizione DOS sulla mappa SI.TA.C.
 *
 * © 2024-2025 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React, { useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

interface DoSMarkerLayerProps {
  lat: number;
  lng: number;
  timestamp?: string;
  onAggiornaPosizione: () => void;
  onMoveDos: (lat: number, lng: number) => void;
  gpsLoading?: boolean;
  readOnly?: boolean;
}

(function injectDoSStyles() {
  if (document.getElementById('dos518-dos-marker-styles')) return;
  const s = document.createElement('style');
  s.id = 'dos518-dos-marker-styles';
  s.textContent = `
    @keyframes dos-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.7), 0 2px 10px rgba(0,0,0,0.55); }
      50%      { box-shadow: 0 0 0 10px rgba(251,191,36,0), 0 2px 10px rgba(0,0,0,0.55); }
    }
    .dos-marker-pulse {
      animation: dos-pulse 2s ease-in-out infinite !important;
    }
  `;
  document.head.appendChild(s);
})();

function buildDoSIcon(): L.DivIcon {
  const html = `
    <div style="position:relative;width:40px;height:40px;">
      <div class="dos-marker-pulse" style="
        width:40px;height:40px;
        background:linear-gradient(135deg,#f59e0b,#d97706);
        border:3px solid rgba(255,255,255,0.95);
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-size:20px;line-height:1;
        box-shadow:0 2px 10px rgba(0,0,0,0.55);
        user-select:none;
      ">🎖️</div>
      <div style="position:absolute;bottom:-20px;left:50%;transform:translateX(-50%);background:rgba(15,23,42,0.92);color:#fcd34d;font-size:8.5px;padding:2px 7px;border-radius:3px;white-space:nowrap;pointer-events:none;font-weight:800;letter-spacing:0.5px;border:1px solid rgba(251,191,36,0.4);">DOS</div>
    </div>`;
  return L.divIcon({ html, iconSize: [40, 40], iconAnchor: [20, 20], className: '' });
}

const DoSMarkerLayer: React.FC<DoSMarkerLayerProps> = ({
  lat,
  lng,
  timestamp,
  onAggiornaPosizione,
  onMoveDos,
  gpsLoading = false,
  readOnly = false,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Marker
      position={[lat, lng]}
      icon={buildDoSIcon()}
      draggable={!readOnly}
      eventHandlers={{
        dragend(e) {
          const pos = e.target.getLatLng();
          onMoveDos(pos.lat, pos.lng);
        },
        click() {
          setOpen(true);
        },
      }}
    >
      <Popup onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
        <div style={{ minWidth: 180, fontFamily: 'sans-serif', fontSize: '0.85rem' }}>
          <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 6, color: '#92400e' }}>
            🎖️ Posizione DOS
          </div>
          <div style={{ color: '#475569', marginBottom: 4, fontSize: '0.78rem' }}>
            {lat.toFixed(6)}, {lng.toFixed(6)}
          </div>
          {timestamp && (
            <div style={{ color: '#94a3b8', marginBottom: 10, fontSize: '0.73rem' }}>
              Aggiornato: {timestamp}
            </div>
          )}
          {!readOnly && (
            <button
              onClick={onAggiornaPosizione}
              disabled={gpsLoading}
              style={{
                width: '100%', padding: '6px 10px',
                background: gpsLoading ? '#e5e7eb' : '#f59e0b',
                color: gpsLoading ? '#9ca3af' : '#fff',
                border: 'none', borderRadius: 5, cursor: gpsLoading ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem', fontWeight: 700,
              }}
            >
              {gpsLoading ? '⌛ Acquisizione GPS...' : '📡 Aggiorna posizione GPS'}
            </button>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

export default DoSMarkerLayer;
